import React from 'react';
import { fetcher } from './axios';
import Compressor from 'compressorjs';
import { getImageUrl } from './getImageUrl';
import { TemplateVarsData } from 'types/templates/templates-menu';
import {
  FetchSendTemplateMessageParams,
  getTemplateFormattedObject,
  GetTemplateFormattedObjectParams,
} from 'api/templates';
import {
  WhatsappFrontTemplateObject,
  WhatsappOriginalTemplate,
  WhatsappTemplate,
  WhatsappTemplateMessageComponents,
} from 'types/templates';
import { store } from 'store';

export function getNestedValue<T>(obj: T, path: string): any {
  return path.split('.').reduce((acc: any, key) => acc && acc[key], obj);
}
export function capitalizeFirstLetter(val: string) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

//* Languages
export function isEnglishString(str: string) {
  const englishRegex = /^[a-zA-Z0-9\s.,!?]+$/;
  return englishRegex.test(str);
}
export function isHebrewString(str: string) {
  const hebrewRegex = /[\u0590-\u05FF]+/;
  return hebrewRegex.test(str);
}

//* JSON
export function IsJsonString(str: string | null | undefined): boolean {
  if (typeof str !== 'string') return false;
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
}
function escapeJsonString(value: string) {
  if (typeof value === 'string') {
    return value
      .replace(/\\/g, '\\\\') // Escaping backslash
      .replace(/"/g, '\\"') // Escaping quotes
      .replace(/\n/g, '\\n') // Escaping breaklines
      .replace(/\r/g, '\\r') //
      .replace(/\t/g, '\\t'); // Escaping tabs
  }
  return value; // If not string - return value itself
}
export const sanitizeJson: (obj: any) => typeof obj = (obj) => {
  if (typeof obj === 'string') {
    return escapeJsonString(obj);
  } else if (Array.isArray(obj)) {
    return obj.map(sanitizeJson);
  } else if (typeof obj === 'object' && obj !== null) {
    const sanitizedObject: {
      [key: string]: any;
    } = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        sanitizedObject[key] = sanitizeJson(obj[key]);
      }
    }
    return sanitizedObject;
  }
  return obj;
};
export function safeParseJSON(jsonString: string | null | undefined) {
  try {
    if (!jsonString) return null;
    if (!IsJsonString(jsonString)) return jsonString;
    return JSON.parse(jsonString);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // console.error('Invalid JSON format:', jsonString, error);
    return null;
  }
}

//* Generate ID
export function generateUniqueId(existingIds: string[] = [], length: number = 5) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;

  let uniqueId = '';
  let isUnique = false;

  while (!isUnique) {
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    if (!existingIds.includes(result)) {
      uniqueId = result;
      isUnique = true;
    }
  }

  return uniqueId;
}
export const generateIncrementalId = (items: any[]) => {
  if (items.length === 0) return 1; // if empty array - return 1 as first ID
  const maxId = Math.max(...items.map((item) => item.id));
  return maxId + 1; // New id is the max id + 1
};

//* Colors
export function getContrastYIQ(hexcolor: string) {
  if (!hexcolor) return hexcolor;

  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  return yiq >= 128 ? 'black' : 'white';
}

//* Links
export async function getFileLinkFromServer(e: React.ChangeEvent<HTMLInputElement>) {
  try {
    const file = e.target.files?.[0];
    if (!file) {
      console.error('No file selected');
      return null;
    }

    const formData = new FormData();
    if (file.type.startsWith('image/')) {
      formData.append('image', file);
      const data = await fetcher('post', ['/api/services/GetImageLink', { data: formData }], 'history');
      if (data?.result === true) {
        return {
          Name: file.name,
          Link: data.link,
        };
      } else return null;
    } else {
      formData.append('file', file);
      const data = await fetcher('post', ['/api/whatsapp/SaveFile', { data: formData }], 'history');
      if (data?.result === true) {
        return {
          Name: file.name,
          Link: data.link,
        };
      } else return null;
    }
  } catch (error) {
    console.error(error);
    return null;
  } finally {
    e.target.value = '';
  }
}
export const isValidUrl = (string: string) => {
  let url;

  try {
    url = new URL(string);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
};
export function getPathname(): string {
  return window.location.pathname;
}

//* HTML
export function getElementOffset(el: Element) {
  const box = el.getBoundingClientRect();

  return {
    top: box.top + window.scrollY,
    right: box.right + window.scrollX,
    bottom: box.bottom + window.scrollY,
    left: box.left + window.scrollX,
  };
}
export const clickPreventDefaultAndStopPropagation = (e: React.MouseEvent | React.ChangeEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

//* Dates
export const displayMessageDate = (date: string) => {
  const targetDate = new Date(date);
  const currentDate = new Date();
  targetDate.setHours(0, 0, 0, 0);
  currentDate.setHours(0, 0, 0, 0);

  const timeDifferenceInDays = (currentDate.getTime() - targetDate.getTime()) / (24 * 60 * 60 * 1000);
  return timeDifferenceInDays;
};

/** תזכורת ל-tooltip — שעה בלבד אם היום, אחרת dd/mm/yyyy hh:mm.
 *  ההמרה דרך Date (שעון מקומי) ולא חיתוך מחרוזת: ה-ISO מנורמל ל-UTC
 *  (normalizeSnoozeEndTimeFromApi → toISOString), ולכן slice(11,16) הציג את ספרות
 *  ה-UTC — תזכורת שנקבעה ל-18:07 הוצגה 15:07. */
export function formatReminderDateTimePipo(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const timePart = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (displayMessageDate(iso) === 0) {
    return timePart;
  }
  const datePart = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  return `${datePart} ${timePart}`;
}

/** תווית בועת תזכורת — שעה בלבד אם היום, אחרת "hh:mm · dd/mm" (שעון מקומי, ראה לעיל) */
export function formatReminderBubbleLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const timePart = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (displayMessageDate(iso) === 0) {
    return timePart;
  }
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${timePart} · ${day}/${month}`;
}

export function convertSecondstoTime(secondsNumber: number) {
  const dateObj = new Date(secondsNumber * 1000);
  // const hours = dateObj.getUTCHours();
  const minutes = dateObj.getUTCMinutes();
  const seconds = dateObj.getSeconds();

  const timeString = minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0');

  return timeString;
}
export const getDateParts = (dateParam: Date | null = null) => {
  const date = dateParam || new Date();
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(7, '0');

  return {
    day,
    month,
    year,
    hours,
    minutes,
    seconds,
    milliseconds,
  };
};
export function getCurrentDateFormatted() {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const formattedMonth = month < 10 ? `0${month}` : month;
  const formattedDay = day < 10 ? `0${day}` : day;

  return `${year}-${formattedMonth}-${formattedDay}`;
}

//* Files
export const getFileType = (str: string) => {
  if (!str) return null;
  const splitFileName = str.split('.');
  const fileType = splitFileName[splitFileName.length - 1].toLowerCase();
  return fileType;
};
export function getFileFormatFromURL(url: string) {
  if (!url) return url;
  const pathWithoutQuery = url.split('?')[0];
  const fileExtension = pathWithoutQuery.split('.').pop();
  return fileExtension?.toLowerCase();
}
export function getFileNameFromURL(url: string) {
  if (!url) return url;
  const fileExtension = url.split('/').pop();
  return fileExtension?.toLowerCase();
}
export const getFileTypeByLink = (link: string) => {
  const format = '.' + getFileFormatFromURL(link);
  let mediaType = '';

  if (
    format.toLowerCase() === '.png' ||
    format.toLowerCase() === '.jpg' ||
    format.toLowerCase() === '.jpeg' ||
    format.toLowerCase() === '.webp'
  ) {
    mediaType = 'image';
  } else {
    var fileType = '';
    switch (format.toLowerCase()) {
      case '.pdf':
        fileType = 'pdf';
        break;
      case '.xlsx':
        fileType = 'xlsx';
        break;
      case '.mp3':
        fileType = 'audio';
        break;
      case '.m4a':
        fileType = 'audio';
        break;
      case '.z': /// android
        fileType = 'audio';
        //fileLink = model.imageLink.Replace(".z", ".ogg");
        break;
      case '.acc':
        fileType = 'audio';
        break;
      case '.aac':
        fileType = 'audio';
        break;
      case '.3gp':
        fileType = 'audio';
        break;
      case '.amr':
        fileType = 'audio';
        break;
      case '.ogv':
        fileType = 'audio';
        break;
      case '.mp4':
        fileType = 'video';
        break;
      case '.zip':
        fileType = 'zip';
        break;
      case '.mpeg':
        fileType = 'video';
        break;
      case '.mov':
        fileType = 'video';
        break;
      case '.docx':
        fileType = 'docx';
        break;
      case '.gif':
        fileType = 'image';
        break;
      case '.png':
        fileType = 'image';
        break;
      case '.jpg':
        fileType = 'image';
        break;
      case '.jpeg':
        fileType = 'image';
        break;
      default:
        fileType = 'document';
        break;
    }
    mediaType = fileType;
  }

  return mediaType;
};

/** Aligns with SendOutgoingMessageWithFile — server rejects generic "media" for WhatsApp sends. */
export function mapFileKindToWhatsappOutgoingMessageType(
  detected: string,
): 'image' | 'video' | 'audio' | 'document' {
  const d = (detected || '').toLowerCase();
  if (d === 'image' || d === 'gif') return 'image';
  if (d === 'video') return 'video';
  if (d === 'audio') return 'audio';
  return 'document';
}
export function compressImage(file: File | Blob, callback: (result: File | Blob) => void) {
  new Compressor(file, {
    quality: 0.6, // compress level
    maxWidth: 1000,
    maxHeight: 1200,
    success(result) {
      callback(result);
    },
    error(err) {
      console.error('Compressing error: ', err.message);
    },
  });
}

//* Lists & sorting
export function sortListByParam(list: Array<any>, param: string) {
  return [...list].sort((a, b) => a[param] - b[param]);
}
export const isDuplicate = (array: any[], key: string, value: any, index: number) => {
  if (value == null || String(value).trim() === '') {
    return false;
  }

  if (index <= 0 || index >= array.length) {
    return false;
  }

  for (let i = 0; i < index; i++) {
    if (array[i][key] === value) {
      return true;
    }
  }

  return false;
};

//* Notification — see utils/browserNotifications.ts
export {
  browserPermissionRequest,
  getBrowserNotifStatus,
  handleBrowserNotification,
  preloadNotificationIcon,
  prepareBrowserNotifications,
  runNotificationTest,
  type NotificationTestResult,
} from './browserNotifications';

/**
 * Meta Cloud API: type ברכיבים הוא lowercase; sub_type חייב להיות lowercase (url, quick_reply) —
 * ב־GetTemplateFormattedObject לעיתים מגיע URL/QUICK_REPLY מאותיות גדולות ואז 132000.
 * index לכפתור חייב מחרוזת ("0", "1") לא מספר.
 * רכיבים ללא parameters — לא שולחים (מפרט Meta).
 */
function mapMetaButtonSubType(sub: unknown): string | undefined {
  if (sub == null || typeof sub !== 'string') return undefined;
  const u = sub.toUpperCase();
  const map: Record<string, string> = {
    URL: 'url',
    QUICK_REPLY: 'quick_reply',
    PHONE_NUMBER: 'phone_number',
    COPY_CODE: 'copy_code',
    FLOW: 'flow',
  };
  return map[u] ?? sub.toLowerCase();
}

function normalizeTemplateParameterForSend(p: Record<string, unknown>): Record<string, unknown> {
  const out = { ...p };
  if (typeof out.type === 'string') out.type = out.type.toLowerCase();
  return out;
}

function buildWhatsappTemplateComponentsArrayForSend(
  components: WhatsappFrontTemplateObject['template']['components'],
): FetchSendTemplateMessageParams['components'] {
  return components
    .map((comp) => {
      const c = comp as Record<string, unknown>;
      const rawParams = Array.isArray(c.parameters) ? c.parameters : [];
      const parameters = rawParams.map((p) =>
        p && typeof p === 'object' ? normalizeTemplateParameterForSend(p as Record<string, unknown>) : p,
      );
      const base: Record<string, unknown> = {
        type: typeof c.type === 'string' ? c.type.toLowerCase() : c.type,
        parameters,
      };
      const st = mapMetaButtonSubType(c.sub_type);
      if (st != null) base.sub_type = st;
      if (c.index != null) base.index = String(c.index);
      return base as FetchSendTemplateMessageParams['components'][number];
    })
    .filter((row) => (row.parameters?.length ?? 0) > 0);
}

//* Messages and templates
export const formSendTemplateObject = async (
  to: string,
  varsData: TemplateVarsData,
  template: WhatsappTemplate | undefined,
  originalTemplate: WhatsappOriginalTemplate,
  fileName?: string,
) => {
  try {
    const formattedParams: Record<TemplateVarsData[0]['name'], string> = varsData.reduce(
      (acc, param) => {
        acc[param.name] = param.text || '';
        return acc;
      },
      {} as Record<TemplateVarsData[0]['name'], string>,
    );

    const params: GetTemplateFormattedObjectParams = {
      templateObject: template,
      templateOriginalObject: originalTemplate,
      to: to,
      templateVars: formattedParams,
    };

    if (Object.keys(formattedParams).some((objKey) => objKey === 'data') && fileName) {
      params.fileName = fileName;
    }

    const object = await getTemplateFormattedObject(params);

    if (object) {
      const { frontTemplateObj, components } = object;

      const componentsArray = buildWhatsappTemplateComponentsArrayForSend(frontTemplateObj.template.components);

      let sendParams: FetchSendTemplateMessageParams = {
        id: to,
        templateName: frontTemplateObj.template.name,
        languageCode: frontTemplateObj.template.language.code,
        /** שטוח BODY/HEADER — כמו web2; מערך components נפרד ל־Meta */
        message: JSON.stringify(components),
        components: componentsArray,
      };

      return { sendParams, frontTemplateObj, components, formattedParams };
    } else {
      return null;
    }
  } catch (error) {
    console.warn('formSendTemplateObject error: ', error);
    return null;
  }
};

/**
 * טיוטה כש־GetTemplateFormattedObject לא מחזיר מבנה (או לשמירה לפני השלמת פרמטרים).
 * מאפשר לשמור בחירת תבנית בשלב ולהשלים פרמטרים מאוחר יותר.
 */
export function buildDraftTemplateSendParamsFallback(
  to: string,
  originalTemplate: WhatsappOriginalTemplate,
): {
  sendParams: FetchSendTemplateMessageParams;
  frontTemplateObj: WhatsappFrontTemplateObject | null;
  components: WhatsappTemplateMessageComponents | null;
  formattedParams: Record<TemplateVarsData[0]['name'], string>;
} {
  const lang = originalTemplate.language || 'he';
  const sendParams: FetchSendTemplateMessageParams = {
    id: to,
    templateName: originalTemplate.name,
    languageCode: lang,
    message: '{}',
    components: [],
  };
  return {
    sendParams,
    frontTemplateObj: null,
    components: null,
    formattedParams: {} as Record<TemplateVarsData[0]['name'], string>,
  };
}
